import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { formatDate } from '../utils/dateFormat';

/* ============================================================================
 * Badges
 * ========================================================================== */
function ActiveBadge({ active }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
      active ? 'bg-emerald-50 text-emerald-700' : 'bg-secondary-100 text-secondary-500'
    }`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function MandatoryBadge({ mandatory }) {
  if (!mandatory) return null;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">Required</span>;
}

function BoardBadge({ board }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 uppercase tracking-wide">
      {board || 'v3'}
    </span>
  );
}

function RolloutStatusBadge({ status }) {
  const map = {
    pending:     'bg-amber-50 text-amber-700',
    notified:    'bg-amber-50 text-amber-700',
    downloading: 'bg-blue-50 text-blue-700',
    applying:    'bg-blue-50 text-blue-700',
    installing:  'bg-blue-50 text-blue-700',
    success:     'bg-emerald-50 text-emerald-700',
    completed:   'bg-emerald-50 text-emerald-700',
    failed:      'bg-red-50 text-red-700',
    cancelled:   'bg-secondary-100 text-secondary-500',
    reverted:    'bg-orange-50 text-orange-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-secondary-100 text-secondary-500'}`}>
      {status}
    </span>
  );
}

/* Collapse/expand chevron — points down when open, right when collapsed */
function Chevron({ collapsed }) {
  return (
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      className={`w-4 h-4 text-secondary-400 shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/* ============================================================================
 * Form defaults
 * ========================================================================== */
const emptyForm = {
  version: '',
  board_model: 'v3',
  variant_label: '',
  release_date: '',
  download_url: '',
  checksum: '',
  release_notes: '',
  mandatory: false,
};

const ALL_BOARDS = '__all__';

/* ============================================================================
 * Page
 * ========================================================================== */
export default function Firmware() {
  /* ---- data ---- */
  const [firmware, setFirmware] = useState([]);
  const [rollouts, setRollouts] = useState([]);
  const [boardModels, setBoardModels] = useState([]);
  const [loadingFw, setLoadingFw] = useState(true);
  const [loadingRollouts, setLoadingRollouts] = useState(true);

  /* ---- filter ---- */
  const [boardFilter, setBoardFilter] = useState(ALL_BOARDS);

  /* ---- collapsible tables ---- */
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);
  const [rolloutCollapsed, setRolloutCollapsed] = useState(false);

  /* ---- create / edit form ---- */
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  /* ---- push update panel ---- */
  const [pushFwId, setPushFwId] = useState('');
  const [pushMode, setPushMode] = useState('board'); // 'board' | 'devices' | 'all' | 'firmware'
  const [pushBoard, setPushBoard] = useState('v3');
  const [pushDeviceIds, setPushDeviceIds] = useState('');
  const [pushError, setPushError] = useState('');
  const [pushSuccess, setPushSuccess] = useState('');
  const [pushing, setPushing] = useState(false);

  /* ---- device picker for 'devices' mode ---- */
  const [deviceList, setDeviceList] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');

  /* ---- firmware version filter for 'firmware' mode ---- */
  const [firmwareVersions, setFirmwareVersions] = useState([]);
  const [pushCurrentFw, setPushCurrentFw] = useState('');

  /* ============================================================================
   * Fetchers
   * ========================================================================== */
  const fetchFirmware = useCallback(async () => {
    setLoadingFw(true);
    try {
      const { data } = await api.get('/ota/firmware');
      setFirmware(Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []);
    } catch {} finally {
      setLoadingFw(false);
    }
  }, []);

  const fetchRollouts = useCallback(async () => {
    setLoadingRollouts(true);
    try {
      const { data } = await api.get('/ota/rollouts');
      setRollouts(Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []);
    } catch {} finally {
      setLoadingRollouts(false);
    }
  }, []);

  const fetchBoardModels = useCallback(async () => {
    try {
      const { data } = await api.get('/ota/board-models');
      setBoardModels(Array.isArray(data.data) ? data.data : []);
    } catch {}
  }, []);

  const fetchFirmwareVersions = useCallback(async () => {
    try {
      const { data } = await api.get('/ota/firmware-versions');
      setFirmwareVersions(Array.isArray(data.data) ? data.data : []);
    } catch {}
  }, []);

  const fetchDeviceList = useCallback(async (params = {}) => {
    setLoadingDevices(true);
    try {
      const qs = new URLSearchParams(params).toString();
      const { data } = await api.get(`/ota/devices-simple${qs ? `?${qs}` : ''}`);
      setDeviceList(Array.isArray(data.data) ? data.data : []);
    } catch {} finally {
      setLoadingDevices(false);
    }
  }, []);

  useEffect(() => {
    fetchFirmware();
    fetchRollouts();
    fetchBoardModels();
    fetchFirmwareVersions();
  }, [fetchFirmware, fetchRollouts, fetchBoardModels, fetchFirmwareVersions]);

  // Load device list when switching to 'devices' mode
  useEffect(() => {
    if (pushMode === 'devices') fetchDeviceList();
  }, [pushMode, fetchDeviceList]);

  /* ============================================================================
   * Derived data
   * ========================================================================== */
  const visibleFirmware = useMemo(() => {
    if (boardFilter === ALL_BOARDS) return firmware;
    return firmware.filter(fw => (fw.board_model || 'v3') === boardFilter);
  }, [firmware, boardFilter]);

  const pushableFirmware = useMemo(() => {
    // For the push panel, when pushing to a specific board only show firmware
    // for that board. For "all boards" or "device list" mode, show everything.
    if (pushMode === 'board') {
      return firmware.filter(fw => (fw.board_model || 'v3') === pushBoard);
    }
    return firmware;
  }, [firmware, pushMode, pushBoard]);

  // Distinct list of board models from data + always offer v3 / v4
  const knownBoards = useMemo(() => {
    const fromApi = new Set(boardModels.map(b => b.board_model));
    const fromFw = new Set(firmware.map(fw => fw.board_model || 'v3'));
    const all = new Set([...fromApi, ...fromFw, 'v3']);
    return Array.from(all).sort();
  }, [boardModels, firmware]);

  /* ============================================================================
   * Form: create / edit firmware release
   * ========================================================================== */
  const openAddForm = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      board_model: boardFilter === ALL_BOARDS ? 'v3' : boardFilter,
    });
    setFormError('');
    setShowForm(true);
  };

  const openEditForm = (fw) => {
    setEditingId(fw.id);
    setForm({
      version: fw.version || '',
      board_model: fw.board_model || 'v3',
      variant_label: fw.variant_label || '',
      release_date: fw.release_date ? fw.release_date.slice(0, 10) : '',
      download_url: fw.download_url || '',
      checksum: fw.checksum || '',
      release_notes: fw.release_notes || '',
      mandatory: !!fw.mandatory,
    });
    setFormError('');
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSaving(true);
    try {
      const payload = { ...form };
      if (editingId) {
        await api.put(`/ota/firmware/${editingId}`, payload);
      } else {
        await api.post('/ota/firmware', payload);
      }
      setShowForm(false);
      fetchFirmware();
      fetchBoardModels();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setFormSaving(false);
    }
  };

  /* ============================================================================
   * Push update
   * ========================================================================== */
  const handlePush = async (e) => {
    e.preventDefault();
    setPushError('');
    setPushSuccess('');

    if (!pushFwId) {
      setPushError('Select a firmware build first.');
      return;
    }

    const selectedFw = firmware.find(fw => String(fw.id) === String(pushFwId));
    if (!selectedFw) {
      setPushError('Selected firmware not found.');
      return;
    }

    setPushing(true);
    try {
      let payload = { firmware_version_id: Number(pushFwId) };

      if (pushMode === 'devices') {
        // Use selected devices from picker, or fall back to typed IDs
        const pickedIds = Array.from(selectedDevices);
        const typedIds = pushDeviceIds.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
        const ids = pickedIds.length > 0 ? pickedIds : typedIds;
        if (ids.length === 0) {
          setPushError('Select at least one device.');
          setPushing(false);
          return;
        }
        payload.device_ids = ids;
      } else if (pushMode === 'firmware') {
        if (!pushCurrentFw) {
          setPushError('Select a current firmware version to target.');
          setPushing(false);
          return;
        }
        payload.current_firmware = pushCurrentFw;
      } else if (pushMode === 'all') {
        payload.board_model = '*';
      } else {
        // 'board'
        payload.board_model = pushBoard;
      }

      const { data } = await api.post('/ota/assign-bulk', payload);
      const summary = data.summary || {};
      const total = summary.total ?? (data.results?.length || 0);
      const ok = summary.assigned ?? (data.results?.filter(r => r.status === 'assigned').length || 0);
      const failed = total - ok;
      if (total === 0) {
        setPushSuccess('No matching devices.');
      } else if (failed === 0) {
        setPushSuccess(`Pushed ${selectedFw.version} (${selectedFw.board_model}) to ${ok} device(s).`);
      } else {
        setPushSuccess(`Pushed to ${ok}/${total} devices. ${failed} failed.`);
      }
      setPushDeviceIds('');
      setSelectedDevices(new Set());
      fetchRollouts();
      fetchFirmwareVersions();
    } catch (err) {
      setPushError(err.response?.data?.error || err.message || 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  /* ============================================================================
   * Rollout actions
   * ========================================================================== */
  const handleCancel = async (device_id) => {
    try {
      await api.post('/ota/cancel', { device_id });
      fetchRollouts();
    } catch {}
  };

  const handleRevert = async (device_id) => {
    try {
      await api.post('/ota/revert', { device_id });
      fetchRollouts();
    } catch {}
  };

  /* ============================================================================
   * Render
   * ========================================================================== */
  return (
    <div className="space-y-6">
      {/* ----- Page header ----- */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Firmware &amp; OTA</h1>
          <p className="text-secondary-500 text-sm mt-1">
            Manage firmware builds per board model and push updates to your fleet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Board filter */}
          <select
            value={boardFilter}
            onChange={(e) => setBoardFilter(e.target.value)}
            className="px-3 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
            title="Filter library by board model"
          >
            <option value={ALL_BOARDS}>All boards</option>
            {knownBoards.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <button
            onClick={openAddForm}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition"
          >
            + New Build
          </button>
        </div>
      </div>

      {/* ----- Board summary chips ----- */}
      {boardModels.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {boardModels.map(b => (
            <button
              key={b.board_model}
              onClick={() => setBoardFilter(b.board_model)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                boardFilter === b.board_model
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'bg-white border-secondary-200 text-secondary-600 hover:bg-secondary-50'
              }`}
            >
              <span className="uppercase tracking-wide">{b.board_model}</span>
              <span className="ml-2 text-secondary-400">{b.device_count} device{b.device_count === 1 ? '' : 's'}</span>
            </button>
          ))}
          {boardFilter !== ALL_BOARDS && (
            <button
              onClick={() => setBoardFilter(ALL_BOARDS)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-secondary-200 text-secondary-500 hover:bg-secondary-50"
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {/* ----- Inline create / edit form ----- */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-card p-5">
          <h2 className="font-semibold text-secondary-900 mb-4">
            {editingId ? 'Edit Firmware Build' : 'New Firmware Build'}
          </h2>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Board Model *</label>
                <input
                  name="board_model" value={form.board_model} onChange={handleFormChange} required
                  placeholder="v3"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
                <p className="text-[11px] text-secondary-400 mt-1">e.g. v3, v4. Same version may exist on multiple boards.</p>
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Version *</label>
                <input
                  name="version" value={form.version} onChange={handleFormChange} required
                  placeholder="e.g. 4.0.6a"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
                <p className="text-[11px] text-secondary-400 mt-1">Use suffixes for variants (4.0.6a vs 4.0.6b).</p>
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Variant Label</label>
                <input
                  name="variant_label" value={form.variant_label} onChange={handleFormChange}
                  placeholder="e.g. low-power, debug"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Release Date *</label>
                <input
                  name="release_date" type="date" value={form.release_date} onChange={handleFormChange} required
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs text-secondary-500 mb-1">Download URL</label>
                <input
                  name="download_url" value={form.download_url} onChange={handleFormChange}
                  placeholder="/firmware-bin/firmware-4.0.6a.bin"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs text-secondary-500 mb-1">Checksum (SHA-256)</label>
                <input
                  name="checksum" value={form.checksum} onChange={handleFormChange}
                  placeholder="64-char hex"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none font-mono"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-secondary-700 cursor-pointer">
                  <input
                    name="mandatory" type="checkbox" checked={form.mandatory} onChange={handleFormChange}
                    className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  Mandatory update
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs text-secondary-500 mb-1">Release Notes</label>
              <textarea
                name="release_notes" value={form.release_notes} onChange={handleFormChange} rows={3}
                placeholder="What changed in this build..."
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-y"
              />
            </div>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <div className="flex gap-2">
              <button
                type="submit" disabled={formSaving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition disabled:opacity-50"
              >
                {formSaving ? 'Saving...' : editingId ? 'Update Build' : 'Create Build'}
              </button>
              <button
                type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-secondary-200 text-secondary-600 rounded-lg text-sm hover:bg-secondary-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ----- Firmware Library ----- */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-secondary-100 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setLibraryCollapsed(v => !v)}
            className="flex items-center gap-2 text-left"
            title={libraryCollapsed ? 'Expand library' : 'Collapse library'}
          >
            <Chevron collapsed={libraryCollapsed} />
            <div>
              <h2 className="font-semibold text-secondary-900">Firmware Library</h2>
              <p className="text-xs text-secondary-400 mt-0.5">
                {boardFilter === ALL_BOARDS
                  ? `${visibleFirmware.length} build(s) across all boards`
                  : `${visibleFirmware.length} build(s) for board ${boardFilter}`}
              </p>
            </div>
          </button>
          <button
            onClick={fetchFirmware}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Refresh
          </button>
        </div>
        {!libraryCollapsed && (
        <div className="max-h-[340px] overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 sticky top-0 z-10">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Board</th>
                <th className="px-5 py-3 text-left font-medium">Version</th>
                <th className="px-5 py-3 text-left font-medium">Variant</th>
                <th className="px-5 py-3 text-left font-medium">Release Date</th>
                <th className="px-5 py-3 text-left font-medium">Mandatory</th>
                <th className="px-5 py-3 text-left font-medium">Active</th>
                <th className="px-5 py-3 text-left font-medium">Notes</th>
                <th className="px-5 py-3 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loadingFw && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loadingFw && visibleFirmware.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-secondary-400">No firmware builds found</td></tr>
              )}
              {!loadingFw && visibleFirmware.map((fw) => (
                <tr key={fw.id} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3"><BoardBadge board={fw.board_model} /></td>
                  <td className="px-5 py-3 font-mono font-medium text-secondary-900">{fw.version}</td>
                  <td className="px-5 py-3 text-secondary-500 text-xs">{fw.variant_label || '--'}</td>
                  <td className="px-5 py-3 text-secondary-600 text-xs whitespace-nowrap">{formatDate(fw.release_date)}</td>
                  <td className="px-5 py-3"><MandatoryBadge mandatory={fw.mandatory} /></td>
                  <td className="px-5 py-3"><ActiveBadge active={fw.is_active ?? fw.active ?? true} /></td>
                  <td className="px-5 py-3 text-secondary-500 max-w-xs truncate">{fw.release_notes || '--'}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => openEditForm(fw)}
                        className="px-2.5 py-1 text-xs bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setPushFwId(String(fw.id));
                          setPushMode('board');
                          setPushBoard(fw.board_model || 'v3');
                          setPushError('');
                          setPushSuccess('');
                          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                        }}
                        className="px-2.5 py-1 text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition font-medium"
                      >
                        Push
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* ----- Push Update Panel ----- */}
      <div className="bg-white rounded-xl shadow-card p-5">
        <h2 className="font-semibold text-secondary-900 mb-1">Push Firmware Update</h2>
        <p className="text-xs text-secondary-400 mb-4">
          Pick a build and a target. You can push to a board model, specific devices, or all devices running a particular firmware.
        </p>
        <form onSubmit={handlePush} className="space-y-4">
          {/* Mode selector */}
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'board',    label: 'By board model' },
              { id: 'firmware', label: 'By current firmware' },
              { id: 'devices',  label: 'Select devices' },
              { id: 'all',      label: 'All devices' },
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => { setPushMode(opt.id); setPushFwId(''); setSelectedDevices(new Set()); setPushCurrentFw(''); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                  pushMode === opt.id
                    ? 'bg-primary-50 border-primary-200 text-primary-700'
                    : 'bg-white border-secondary-200 text-secondary-600 hover:bg-secondary-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {pushMode === 'board' && (
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Target Board</label>
                <select
                  value={pushBoard}
                  onChange={(e) => { setPushBoard(e.target.value); setPushFwId(''); }}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
                >
                  {knownBoards.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            )}

            {pushMode === 'firmware' && (
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Devices currently running</label>
                <select
                  value={pushCurrentFw}
                  onChange={(e) => setPushCurrentFw(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
                >
                  <option value="">Select current firmware...</option>
                  {firmwareVersions.map((fv, i) => (
                    <option key={i} value={fv.firmware_version}>
                      {fv.firmware_version} ({fv.board_model}) — {fv.device_count} device{fv.device_count === 1 ? '' : 's'}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-secondary-400 mt-1">
                  All devices on this version will receive the update.
                </p>
              </div>
            )}

            <div>
              <label className="block text-xs text-secondary-500 mb-1">Push Firmware Build</label>
              <select
                value={pushFwId}
                onChange={(e) => setPushFwId(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
              >
                <option value="">Select firmware...</option>
                {pushableFirmware.map((fw) => (
                  <option key={fw.id} value={fw.id}>
                    {fw.board_model || 'v3'} — {fw.version}{fw.variant_label ? ` (${fw.variant_label})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Device picker for 'devices' mode */}
          {pushMode === 'devices' && (
            <div className="border border-secondary-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-secondary-50 border-b border-secondary-200 flex items-center gap-2">
                <input
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  placeholder="Search devices..."
                  className="flex-1 px-2 py-1 border border-secondary-200 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
                <span className="text-xs text-secondary-400 whitespace-nowrap">
                  {selectedDevices.size} selected
                </span>
                {selectedDevices.size > 0 && (
                  <button type="button" onClick={() => setSelectedDevices(new Set())}
                    className="text-xs text-red-600 hover:text-red-700 font-medium">Clear</button>
                )}
              </div>
              <div className="max-h-52 overflow-y-auto divide-y divide-secondary-100">
                {loadingDevices && <p className="px-3 py-4 text-center text-secondary-400 text-sm">Loading devices...</p>}
                {!loadingDevices && deviceList
                  .filter(d => !deviceSearch || d.device_id.toLowerCase().includes(deviceSearch.toLowerCase()))
                  .map(d => (
                    <label key={d.device_id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary-50 transition ${
                        selectedDevices.has(d.device_id) ? 'bg-primary-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDevices.has(d.device_id)}
                        onChange={() => {
                          setSelectedDevices(prev => {
                            const next = new Set(prev);
                            if (next.has(d.device_id)) next.delete(d.device_id); else next.add(d.device_id);
                            return next;
                          });
                        }}
                        className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-secondary-800">{d.device_id}</span>
                      </div>
                      <span className="text-xs font-mono text-secondary-500">{d.firmware_version || '--'}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        d.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                      }`}>{d.status}</span>
                    </label>
                  ))
                }
                {!loadingDevices && deviceList.length === 0 && (
                  <p className="px-3 py-4 text-center text-secondary-400 text-sm">No devices found</p>
                )}
              </div>
              {/* Select all / select by board shortcuts */}
              <div className="px-3 py-2 bg-secondary-50 border-t border-secondary-200 flex flex-wrap gap-2">
                <button type="button" onClick={() => {
                  const filtered = deviceList.filter(d => !deviceSearch || d.device_id.toLowerCase().includes(deviceSearch.toLowerCase()));
                  setSelectedDevices(new Set(filtered.map(d => d.device_id)));
                }} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                  Select all{deviceSearch ? ' visible' : ''}
                </button>
                <span className="text-secondary-300">|</span>
                {knownBoards.map(b => (
                  <button key={b} type="button" onClick={() => {
                    setSelectedDevices(new Set(deviceList.filter(d => (d.board_model || 'v3') === b).map(d => d.device_id)));
                  }} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                    All {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          {pushError && <p className="text-sm text-red-600">{pushError}</p>}
          {pushSuccess && <p className="text-sm text-emerald-600">{pushSuccess}</p>}

          <button
            type="submit" disabled={pushing}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition disabled:opacity-50"
          >
            {pushing ? 'Pushing...' : 'Push Update'}
          </button>
        </form>
      </div>

      {/* ----- Rollout Status ----- */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-secondary-100 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setRolloutCollapsed(v => !v)}
            className="flex items-center gap-2 text-left"
            title={rolloutCollapsed ? 'Expand rollouts' : 'Collapse rollouts'}
          >
            <Chevron collapsed={rolloutCollapsed} />
            <div>
              <h2 className="font-semibold text-secondary-900">Rollout Status</h2>
              <p className="text-xs text-secondary-400 mt-0.5">{rollouts.length} rollout(s)</p>
            </div>
          </button>
          <button
            onClick={fetchRollouts}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Refresh
          </button>
        </div>
        {!rolloutCollapsed && (
        <div className="max-h-[340px] overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 sticky top-0 z-10">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Device ID</th>
                <th className="px-5 py-3 text-left font-medium">Board</th>
                <th className="px-5 py-3 text-left font-medium">Target</th>
                <th className="px-5 py-3 text-left font-medium">Current</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium">Assigned By</th>
                <th className="px-5 py-3 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loadingRollouts && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loadingRollouts && rollouts.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-secondary-400">No rollouts found</td></tr>
              )}
              {!loadingRollouts && rollouts.map((r, i) => (
                <tr key={r.id || i} className="hover:bg-secondary-50 transition">
                  <td className="px-5 py-3 font-medium text-primary-600">
                    <Link to={`/devices/${r.device_id}`} className="hover:underline">{r.device_id}</Link>
                  </td>
                  <td className="px-5 py-3">
                    <BoardBadge board={r.device_board_model || r.firmware_board_model} />
                  </td>
                  <td className="px-5 py-3 font-mono text-secondary-700">{r.firmware_version || '--'}</td>
                  <td className="px-5 py-3 font-mono text-secondary-500">{r.current_version || '--'}</td>
                  <td className="px-5 py-3"><RolloutStatusBadge status={r.status} /></td>
                  <td className="px-5 py-3 text-secondary-500 text-xs">{r.assigned_by_email || r.assigned_by || '--'}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1.5">
                      {r.status !== 'success' && r.status !== 'completed' && r.status !== 'cancelled' && r.status !== 'reverted' && (
                        <button
                          onClick={() => handleCancel(r.device_id)}
                          className="px-2.5 py-1 text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition font-medium"
                        >
                          Cancel
                        </button>
                      )}
                      {(r.status === 'success' || r.status === 'completed' || r.status === 'failed') && (
                        <button
                          onClick={() => handleRevert(r.device_id)}
                          className="px-2.5 py-1 text-xs bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition font-medium"
                        >
                          Revert
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
